<template>
    <article class="bg-gray-50 rounded-lg shadow-sm" :aria-labelledby="headlineId">
        <div
            class="p-2 grid grid-cols-[[link]_auto_[single]_max-content_[status]_minmax(80px,max-content)_[end]] grid-flow-col items-center gap-12"
        >
            <div class="col-[link] text-sm text-gray-900 flex justify-between font-bold">
                <TextLink :to="linkTarget">
                    {{ linkText }}
                </TextLink>
                <Tooltip v-if="isExternal" :id="externalLinkIconTooltipId">
                    <template #trigger="tooltipProps">
                        <ExternalLinkIcon class="text-gray-500" aria-hidden="true" tabindex="0" v-bind="tooltipProps" />
                    </template>
                    <template #text>{{ $t('external-link') }}</template>
                </Tooltip>
            </div>
            <Tooltip v-if="isSingleProject" class="col-[single]">
                <template #trigger="tooltipProps">
                    <SingleEventIcon
                        v-bind="tooltipProps"
                        class="inline w-5 h-5 text-gray-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 rounded-xs"
                        tabindex="0"
                    />
                </template>
                <template #text>{{ $t('limited-to-single-project') }}</template>
            </Tooltip>
            <div v-else class="col-[single]"></div>
            <Badge
                v-if="series.status"
                class="col-[status] justify-self-end"
                size="Extra-Small"
                :variant="projectSeriesStatusToBadgeVariant(series.status)"
            >
                {{ getProjectSeriesStatusLabel(series.status) }}
            </Badge>
        </div>
    </article>
</template>

<script setup lang="ts">
import Badge from '@/components/Badge/Badge.vue';
import ExternalLinkIcon from '@/components/Icon/ExternalLinkIcon.vue';
import SingleEventIcon from '@/components/Icon/SingleEventIcon.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { isExternalLink } from '@/helpers/isExternalLink';
import { nanoid } from 'nanoid';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { getProjectSeriesStatusLabel, projectSeriesStatusToBadgeVariant } from '../project';
import { ProjectSeriesStatus } from '../types';

type ProjectSeriesCardProps = {
    series: {
        id: string;
        nameShort: string;
        status?: ProjectSeriesStatus;
    };
    linkTarget: RouteLocationRaw;
    linkText: string;
    isSingleProject: boolean;
    helpText?: string;
    hasBorderBottom?: boolean;
};

const props = defineProps<ProjectSeriesCardProps>();

const headlineId = nanoid();

const isExternal = computed(() => isExternalLink(props.linkTarget));
const externalLinkIconTooltipId = nanoid();
</script>
